import type { ReactNode } from 'react';
import { Breadcrumb, Tabs, Tooltip } from 'antd';
import { Link, useLocation } from 'react-router-dom';
import PlannerTab from './PlannerTab';
import AddFilev2 from 'assets/svg/AddFilev2';
import { PlanDetailsProvider, usePlanDetailsContext } from './context/PlanDetailsContext';
import {
    TPO_PLAN_DETAILS_BREADCRUMB,
    TPO_PLAN_DETAILS_BREADCRUMB_GROUP_LINK,
    TPO_PLAN_DETAILS_BREADCRUMB_LOADING,
    TPO_PLAN_DETAILS_BREADCRUMB_PLANS_LINK,
    TPO_PLAN_DETAILS_PAGE,
    TPO_PLAN_DETAILS_SUMMARY,
    TPO_PLAN_DETAILS_SUMMARY_CATEGORY,
    TPO_PLAN_DETAILS_SUMMARY_CUSTOMER,
    TPO_PLAN_DETAILS_SUMMARY_YEAR,
    TPO_PLAN_DETAILS_TAB_CREATE_PLAN,
    TPO_PLAN_DETAILS_TAB_PLAN,
    TPO_PLAN_DETAILS_TABS,
    TPO_PLAN_DETAILS_TITLE,
} from '../config/testIds';
import { useTpoGenConfig } from '../context/CustomerConfigContext';
import { FieldRulesProvider } from '../rules/store/FieldRulesProvider';
import { EditSessionProvider, useEditSession } from './EditSession/EditSessionContext';
import { TPOGEN_BREADCRUMB_CLICKED } from 'utils/mixpanelEventNames';
import { trackTpoGenEvent, TPOGEN_PAGE_NAMES } from '../utils/trackTpoGenEvent';

const handleBreadcrumbLinkClick = () =>
    trackTpoGenEvent(TPOGEN_BREADCRUMB_CLICKED, TPOGEN_PAGE_NAMES.PLAN_DETAIL, {
        source_page: 'plan_detail',
        target_page: 'plans_list',
        object_type: 'plan',
    });

const BreadCrumbComponent = ({ planName }: { planName: string }) => {
    const tpoGenConfig = useTpoGenConfig();
    const { currentPlanDetails, hasUnsavedNoPromoEvent } = usePlanDetailsContext();
    const { state } = useLocation();
    const plansIndexPath = `/tpo-gen/plans-index${state?.fromSearch ?? ''}`;
    const planningGroupName = currentPlanDetails?.planning_group_name ?? '';
    const displayPlanName = currentPlanDetails?.plan_name || planName;
    return (
        <div className="mb-3 min-w-0 flex-1 mr-4" data-testid={TPO_PLAN_DETAILS_BREADCRUMB}>
            <Breadcrumb>
                <Breadcrumb.Item>{tpoGenConfig.labels.common.breadcrumbRoot}</Breadcrumb.Item>
                <Breadcrumb.Item>
                    {hasUnsavedNoPromoEvent ? (
                        <Tooltip title={tpoGenConfig.labels.planDetail.exitWithoutPromoSave}>
                            <span
                                data-testid={TPO_PLAN_DETAILS_BREADCRUMB_PLANS_LINK}
                                data-state-disabled="true"
                                className="text-[#A49FA9] cursor-not-allowed"
                            >
                                {tpoGenConfig.labels.planDetail.breadcrumb}
                            </span>
                        </Tooltip>
                    ) : (
                        <Link
                            to={plansIndexPath}
                            data-testid={TPO_PLAN_DETAILS_BREADCRUMB_PLANS_LINK}
                            onClick={handleBreadcrumbLinkClick}
                        >
                            {tpoGenConfig.labels.planDetail.breadcrumb}
                        </Link>
                    )}
                </Breadcrumb.Item>
                {currentPlanDetails === null ? (
                    <Breadcrumb.Item>
                        <span className="text-[#A49FA9]" data-testid={TPO_PLAN_DETAILS_BREADCRUMB_LOADING}>
                            …
                        </span>
                    </Breadcrumb.Item>
                ) : planningGroupName ? (
                    <Breadcrumb.Item>
                        {hasUnsavedNoPromoEvent ? (
                            <Tooltip title={tpoGenConfig.labels.planDetail.exitWithoutPromoSave}>
                                <span
                                    data-testid={TPO_PLAN_DETAILS_BREADCRUMB_GROUP_LINK}
                                    data-state-disabled="true"
                                    className="text-[#A49FA9] cursor-not-allowed inline-block max-w-[180px] truncate align-bottom"
                                >
                                    {planningGroupName}
                                </span>
                            </Tooltip>
                        ) : (
                            <Tooltip title={planningGroupName}>
                                <Link
                                    data-testid={TPO_PLAN_DETAILS_BREADCRUMB_GROUP_LINK}
                                    to={plansIndexPath}
                                    className="text-sm text-[#18181B] inline-block max-w-[180px] truncate align-bottom"
                                    onClick={handleBreadcrumbLinkClick}
                                >
                                    {planningGroupName}
                                </Link>
                            </Tooltip>
                        )}
                    </Breadcrumb.Item>
                ) : null}
                <Breadcrumb.Item>
                    <Tooltip title={displayPlanName}>
                        <span
                            data-testid={TPO_PLAN_DETAILS_TITLE}
                            className="text-sm text-[#18181B] inline-block max-w-[220px] truncate align-bottom"
                        >
                            {displayPlanName}
                        </span>
                    </Tooltip>
                </Breadcrumb.Item>
            </Breadcrumb>
        </div>
    );
};

const PlanDetailsHeaderComponent = () => {
    const tpoGenConfig = useTpoGenConfig();
    const { currentPlanDetails } = usePlanDetailsContext();
    const customerValue = currentPlanDetails?.dim2_attrs.join(', ') ?? '...';
    const categoryValue = currentPlanDetails?.dim1_attrs.join(', ') ?? '...';
    return (
        <div className="flex-shrink-0 max-w-[45%]" data-testid={TPO_PLAN_DETAILS_SUMMARY}>
            <p className="text-sm text-[#A49FA9] flex items-baseline gap-x-1 flex-wrap">
                <span className="font-semibold whitespace-nowrap">{tpoGenConfig.labels.planDetail.customer}</span>
                <Tooltip title={customerValue}>
                    <span
                        data-testid={TPO_PLAN_DETAILS_SUMMARY_CUSTOMER}
                        className="inline-block max-w-[200px] truncate align-bottom"
                    >
                        {customerValue}
                    </span>
                </Tooltip>
                <span className="font-semibold whitespace-nowrap ml-1">{tpoGenConfig.labels.planDetail.category}</span>
                <Tooltip title={categoryValue}>
                    <span
                        data-testid={TPO_PLAN_DETAILS_SUMMARY_CATEGORY}
                        className="inline-block max-w-[200px] truncate align-bottom"
                    >
                        {categoryValue}
                    </span>
                </Tooltip>
                <span className="font-semibold whitespace-nowrap ml-1">{tpoGenConfig.labels.planDetail.year}</span>
                <span className="whitespace-nowrap" data-testid={TPO_PLAN_DETAILS_SUMMARY_YEAR}>
                    {currentPlanDetails?.plan_year ?? '...'}
                </span>
            </p>
        </div>
    );
};

const PlanDetailsTabComponent = ({
    planName,
    planId,
    navState,
}: {
    planName: string;
    planId: undefined | string;
    navState: any;
}) => {
    const tpoGenConfig = useTpoGenConfig();
    const { currentPlanDetails } = usePlanDetailsContext();
    return (
        <div className="card-container" data-testid={TPO_PLAN_DETAILS_TABS}>
            <Tabs
                items={[
                    {
                        label: (
                            <p
                                data-testid={TPO_PLAN_DETAILS_TAB_PLAN}
                                className="text-sm text-[#18181B] truncate max-w-[200px]"
                            >
                                {currentPlanDetails?.plan_name || planName}
                            </p>
                        ),
                        key: planId ?? '',
                        children: (
                            <PlannerTab
                                planDetails={{
                                    planId: navState?.planId || planId || currentPlanDetails?.plan_id,
                                    planName: currentPlanDetails?.plan_name || navState?.planName,
                                }}
                            />
                        ),
                    },
                    {
                        label: (
                            <div
                                data-testid={TPO_PLAN_DETAILS_TAB_CREATE_PLAN}
                                data-state-disabled="true"
                                className="flex items-center gap-2"
                            >
                                <AddFilev2 />
                                <span className="text-sm font-normal text-[#18181B] ">
                                    {tpoGenConfig.labels.planDetail.createNewPlan}
                                </span>
                            </div>
                        ),
                        key: 'create-new-plan',
                        children: <div>{tpoGenConfig.labels.planDetail.createNewPlan}</div>,
                        disabled: true,
                    },
                ]}
                type="card"
            />
        </div>
    );
};

// Provides the per-session field-rules store, sourced from useTpoGenConfig().fieldRules.
const FieldRulesGate = ({ children }: { children: ReactNode }) => {
    const { fieldRules } = useTpoGenConfig();
    return <FieldRulesProvider rules={fieldRules}>{children}</FieldRulesProvider>;
};

// Reads the edit session so the page root can publish the current mode as a
// stable QA hook, rather than every surface exposing its own flag.
const PlanDetailsShell = ({
    planName,
    planId,
    navState,
}: {
    planName: string;
    planId: undefined | string;
    navState: any;
}) => {
    const { canEdit, isEstablishingEditAccess } = useEditSession();
    return (
        <div
            data-testid={TPO_PLAN_DETAILS_PAGE}
            data-state-mode={canEdit ? 'edit' : 'view'}
            data-state-establishing={isEstablishingEditAccess ? 'true' : undefined}
        >
            <div className="flex flex-row justify-between">
                <BreadCrumbComponent planName={planName} />
                <PlanDetailsHeaderComponent />
            </div>
            <PlanDetailsTabComponent planName={planName} planId={planId} navState={navState} />
        </div>
    );
};

const PlanDetails = () => {
    const { state, pathname } = useLocation();
    const planName = state?.planName;
    const planId = pathname.split('/').pop();
    return (
        <PlanDetailsProvider key={planId}>
            <EditSessionProvider planId={planId} pendingEditAcquire={Boolean(state?.pendingEditAcquire)}>
                <FieldRulesGate>
                    <PlanDetailsShell planName={planName || planId} planId={planId} navState={state} />
                </FieldRulesGate>
            </EditSessionProvider>
        </PlanDetailsProvider>
    );
};

export default PlanDetails;
